import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useNavigate } from 'react-router-dom'
import { documentListViewService } from '../../services/document-list-view.service'
import { statisticsService } from '../../services/statistics.service'
import { Statistics } from '../../types/statistics'
import './StatisticsWidget.scss'

const FILTER_HAS_TAGS_ANY = 22
const FILTER_MIME_TYPE = 47

export const StatisticsWidget: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [loading, setLoading] = useState(false)
  const [statistics, setStatistics] = useState<Statistics>({})

  useEffect(() => {
    loadStatistics()
  }, [])

  const loadStatistics = async () => {
    setLoading(true)
    try {
      let stats = await statisticsService.getStatistics()

      // Process file types - group extras as "Other" if more than 5
      const fileTypeMax = 5
      if (stats.document_file_type_counts && stats.document_file_type_counts.length > fileTypeMax) {
        const others = stats.document_file_type_counts.slice(fileTypeMax)
        stats.document_file_type_counts = stats.document_file_type_counts.slice(0, fileTypeMax)
        stats.document_file_type_counts.push({
          mime_type: t('other'),
          is_other: true,
          mime_type_count: others.reduce((sum, ft) => ft.mime_type_count + sum, 0),
        })
      }

      setStatistics(stats)
    } catch (error) {
    } finally {
      setLoading(false)
    }
  }

  const getFileTypeExtension = (filetype: any): string => {
    // Simple mime type to extension mapping
    const mimeExtensions: { [key: string]: string } = {
      'application/pdf': 'PDF',
      'image/jpeg': 'JPEG',
      'image/jpg': 'JPG',
      'image/png': 'PNG',
      'image/gif': 'GIF',
      'image/tiff': 'TIFF',
      'image/webp': 'WEBP',
      'application/msword': 'DOC',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'DOCX',
    }

    return mimeExtensions[filetype.mime_type]?.toUpperCase() || filetype.mime_type
  }

  const getFileTypeName = (filetype: any): string => {
    return filetype.mime_type
  }

  const getFileTypePercent = (filetype: any): number => {
    if (!statistics?.documents_total) return 0
    return (filetype.mime_type_count / statistics.documents_total) * 100
  }

  const getItemOpacity = (i: number): number => {
    if (!statistics?.document_file_type_counts) return 1
    return 1 - i / statistics.document_file_type_counts.length
  }

  const filterByFileType = (filetype: any) => {
    if (filetype.is_other) return
    documentListViewService.quickFilter([
      {
        rule_type: FILTER_MIME_TYPE,
        value: filetype.mime_type,
      },
    ])
    navigate('/documents')
  }

  const goToInbox = () => {
    if (statistics.inbox_tags && statistics.inbox_tags.length > 0) {
      documentListViewService.quickFilter([
        {
          rule_type: FILTER_HAS_TAGS_ANY,
          value: statistics.inbox_tags.map((tagId) => tagId.toString()).join(','),
        },
      ])
      navigate('/documents')
    }
  }

  return (
    <div className="statistics-widget">
      <div className="statistics-widget__header">
        <div className="statistics-widget__icon">
          <i className="bi bi-graph-up-arrow"></i>
        </div>
        <h5 className="statistics-widget__title">{t('statistics')}</h5>
      </div>
      <div className="statistics-widget__body">
        <div className="statistics-widget__section">
          {loading ? (
            <div className="statistics-widget__grid">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="statistics-widget__skeleton-item">
                  <div className="statistics-widget__skeleton-line"></div>
                  <div className="statistics-widget__skeleton-badge"></div>
                </div>
              ))}
            </div>
          ) : (
            <>
              <div className="statistics-widget__grid">
                {statistics?.documents_inbox !== null && statistics?.documents_inbox !== undefined && (
                  <div
                    className={`statistics-widget__item ${statistics.documents_inbox > 0 ? 'statistics-widget__item--primary' : ''}`}
                    onClick={goToInbox}
                    style={{ cursor: 'pointer' }}
                    title={t('go_to_inbox')}
                  >
                    <div className="statistics-widget__item-content">
                      <div className="statistics-widget__item-icon">
                        <i className="bi bi-inbox"></i>
                      </div>
                      <div className="statistics-widget__item-label">{t('documents_in_inbox')}</div>
                    </div>
                    <span
                      className={`statistics-widget__item-value ${statistics.documents_inbox > 0 ? 'statistics-widget__item-value--primary' : ''}`}
                    >
                      {statistics.documents_inbox}
                    </span>
                  </div>
                )}

                <Link className="statistics-widget__item statistics-widget__item--primary" title={t('go_to_documents')} to="/documents/">
                  <div className="statistics-widget__item-content">
                    <div className="statistics-widget__item-icon">
                      <i className="bi bi-file-earmark-text"></i>
                    </div>
                    <div className="statistics-widget__item-label">{t('total_documents')}</div>
                  </div>
                  <span className="statistics-widget__item-value">{statistics?.documents_total || 0}</span>
                </Link>

                <div className="statistics-widget__item">
                  <div className="statistics-widget__item-content">
                    <div className="statistics-widget__item-icon">
                      <i className="bi bi-fonts"></i>
                    </div>
                    <div className="statistics-widget__item-label">{t('total_characters')}</div>
                  </div>
                  <span className="statistics-widget__item-value">{statistics?.character_count?.toLocaleString() || 0}</span>
                </div>

                {
                  <div className="statistics-widget__item">
                    <div className="statistics-widget__item-content">
                      <div className="statistics-widget__item-icon">
                        <i className="bi bi-hash"></i>
                      </div>
                      <div className="statistics-widget__item-label">{t('current_asn')}</div>
                    </div>
                    <span className="statistics-widget__item-value">{statistics.current_asn}</span>
                  </div>
                }
              </div>

              {!loading && statistics?.document_file_type_counts && statistics.document_file_type_counts.length > 1 && (
                <div className="statistics-widget__filetypes">
                  <div className="statistics-widget__filetypes-title">
                    <i className="bi bi-pie-chart me-2"></i>
                    Tipovi fajlova
                  </div>
                  <div className="statistics-widget__filetypes-chart">
                    {statistics.document_file_type_counts.map((filetype, i) => (
                      <div
                        key={i}
                        className="statistics-widget__filetype-bar"
                        role="progressbar"
                        aria-valuenow={getFileTypePercent(filetype)}
                        aria-valuemin={0}
                        aria-valuemax={100}
                        title={getFileTypeName(filetype)}
                        onClick={() => !filetype.is_other && filterByFileType(filetype)}
                        style={{
                          cursor: filetype.is_other ? 'default' : 'pointer',
                          width: `${getFileTypePercent(filetype)}%`,
                          opacity: getItemOpacity(i),
                        }}
                      />
                    ))}
                  </div>
                  <div className="statistics-widget__filetypes-legend">
                    {statistics.document_file_type_counts.map((filetype, i) => (
                      <div
                        key={i}
                        className={`statistics-widget__filetype-item ${!filetype.is_other ? 'statistics-widget__filetype-item--clickable' : ''}`}
                        onClick={() => !filetype.is_other && filterByFileType(filetype)}
                      >
                        <span
                          className="statistics-widget__filetype-dot"
                          style={{
                            opacity: getItemOpacity(i),
                          }}
                        />
                        <span className="statistics-widget__filetype-label">
                          <span className="statistics-widget__filetype-name">{getFileTypeExtension(filetype)}</span>
                          <span className="statistics-widget__filetype-percent">{getFileTypePercent(filetype).toFixed(1)}%</span>
                        </span>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </>
          )}
        </div>

        <div className="statistics-widget__section statistics-widget__section--secondary">
          {loading ? (
            <div className="statistics-widget__grid">
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="statistics-widget__skeleton-item">
                  <div className="statistics-widget__skeleton-line"></div>
                  <div className="statistics-widget__skeleton-badge"></div>
                </div>
              ))}
            </div>
          ) : (
            <div className="statistics-widget__grid">
              <Link className="statistics-widget__item" to="/tags/">
                <div className="statistics-widget__item-content">
                  <div className="statistics-widget__item-icon">
                    <i className="bi bi-tags"></i>
                  </div>
                  <div className="statistics-widget__item-label">{t('tags')}</div>
                </div>
                <span className="statistics-widget__item-value">{statistics?.tag_count?.toLocaleString() || 0}</span>
              </Link>

              <Link className="statistics-widget__item" to="/correspondents/">
                <div className="statistics-widget__item-content">
                  <div className="statistics-widget__item-icon">
                    <i className="bi bi-person-badge"></i>
                  </div>
                  <div className="statistics-widget__item-label">{t('correspondents')}</div>
                </div>
                <span className="statistics-widget__item-value">{statistics?.correspondent_count?.toLocaleString() || 0}</span>
              </Link>

              <Link className="statistics-widget__item" to="/documenttypes/">
                <div className="statistics-widget__item-content">
                  <div className="statistics-widget__item-icon">
                    <i className="bi bi-file-earmark-text-fill"></i>
                  </div>
                  <div className="statistics-widget__item-label">{t('document_types')}</div>
                </div>
                <span className="statistics-widget__item-value">{statistics?.document_type_count?.toLocaleString() || 0}</span>
              </Link>

              <Link className="statistics-widget__item" to="/storagepaths/">
                <div className="statistics-widget__item-content">
                  <div className="statistics-widget__item-icon">
                    <i className="bi bi-folder"></i>
                  </div>
                  <div className="statistics-widget__item-label">{t('storage_paths')}</div>
                </div>
                <span className="statistics-widget__item-value">{statistics?.storage_path_count?.toLocaleString() || 0}</span>
              </Link>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
